'use client';

// Renders a Gear product's picture: the committed catalogue render first
// (`public/catalog/thumbs/<id>.png`, `pnpm catalog:render --thumbs`), the
// same image its /gear page, the homepage and the share cards show, framed
// for each product by the render script. The live recipe render
// (productThumbnail.ts) is only the fallback for a product that has no
// committed render yet.
//
// It used to be the only path, and its one fixed 3/4 camera sees a flat
// product almost edge-on: every TV in the Media rail came out as a black
// parallelogram (app audit, 2026-09-27), and each card cost a WebGL render
// on the main thread for a picture that already existed as a file.
//
// Every call site keys its list item by product id (the parts picker's rows
// and cards) or fully unmounts between products (GearProductView, SystemView's
// rows),
// so this never has to reset its state for a new product on the SAME
// instance.

import { useEffect, useState } from 'react';
import type { Product } from '@roomtreatment/av-catalog';
import { thumbPath } from '@/components/gear-pages/stage';
import { productThumbnail } from './productThumbnail';

interface Props {
  product: Product;
  className?: string;
}

export default function GearProductThumb({ product, className }: Props) {
  const [missing, setMissing] = useState(false);
  const [live, setLive] = useState<string | null>(null);

  useEffect(() => {
    if (!missing) return;
    let alive = true;
    productThumbnail(product)
      .then((url) => { if (alive) setLive(url); })
      .catch(() => { /* leave the placeholder on failure */ });
    return () => { alive = false; };
  }, [missing, product]);

  if (missing && !live) return <span className={className} aria-hidden />;

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={missing ? live! : thumbPath(product.id)}
      alt=""
      className={className}
      onError={missing ? undefined : () => setMissing(true)}
    />
  );
}
